import React, { useEffect } from "react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { GlobalSettingsProvider } from "./context/GlobalSettingsContext";

import AppRoutes from "./routes/AppRoutes";
import { ThemeProviderWrapper } from "./context/ThemeContext";
import { Provider } from "react-redux";
import { store } from "./shared/store/store";
import Toast from "./pages/components/toast/Toast";
import AOS from "aos";
import "aos/dist/aos.css";
import ErrorBoundary from "./shared/components/ErrorBoundary";

import { enhancePortalTable } from "./shared/components/portalTable";

const App = () => {
  // Turn every data table into labelled cards on mobile and tablet screens.
  // The observer also covers tables that are rendered after an API response.
  useEffect(() => {
    const enhanceResponsiveTables = () => {
      document.querySelectorAll<HTMLTableElement>("table").forEach((table) => {
        const headerRows = table.querySelectorAll<HTMLTableRowElement>("thead tr");
        const headerRow = headerRows.item(headerRows.length - 1);

        if (!headerRow) return;

        const labels = Array.from(
          headerRow.querySelectorAll<HTMLTableCellElement>("th, td")
        ).map((cell) => cell.innerText.trim());

        if (!labels.some(Boolean)) return;

        if (enhancePortalTable(table, labels)) {
          table.classList.add("responsive-stacked-table");
          return;
        }
        table.classList.add("responsive-stacked-table");
        table
          .querySelectorAll<HTMLTableRowElement>("tbody tr")
          .forEach((row) => {
            Array.from(row.children).forEach((cell, index) => {
              if (!(cell instanceof HTMLTableCellElement)) return;

              if (cell.colSpan > 1) {
                cell.setAttribute("data-responsive-full-width", "true");
                cell.removeAttribute("data-label");
                return;
              }

              cell.setAttribute("data-label", labels[index] || "");
            });
          });
      });
    };

    let animationFrame = window.requestAnimationFrame(enhanceResponsiveTables);
    const observer = new MutationObserver(() => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(enhanceResponsiveTables);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(animationFrame);
    };
  }, []);

  // Initialize AOS
  useEffect(() => {
    AOS.init({
      duration: 2000,
      once: false,
      mirror: true,
    });
  }, []);

  // Language change detection and reload logic
  useEffect(() => {
    // Initialize language in localStorage if not present
    if (!localStorage.getItem("language")) {
      localStorage.setItem("language", "en");
    }

    let currentLanguage = localStorage.getItem("language") || "en";

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "language" && e.newValue !== currentLanguage) {
        currentLanguage = e.newValue || "en";
        // Reload the page when language changes
        window.location.reload();
      }
    };

    // Listen for storage events (when localStorage changes in other tabs/windows)
    window.addEventListener("storage", handleStorageChange);

    // Also listen for custom events (when language changes in the same tab)
    const handleLanguageChange = (e: CustomEvent) => {
      if (e.detail?.language !== currentLanguage) {
        currentLanguage = e.detail?.language || "en";
        // Reload the page when language changes
        window.location.reload();
      }
    };

    window.addEventListener(
      "languageChange",
      handleLanguageChange as EventListener
    );

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(
        "languageChange",
        handleLanguageChange as EventListener
      );
    };
  }, []);

  // Close any open dropdown menu when the page/container scrolls,
  // and ensure the select element is blurred so empty placeholders return to center
  useEffect(() => {
    const blurActiveSelect = () => {
      if (document.activeElement instanceof HTMLElement) {
        if (
          document.activeElement.classList.contains("MuiSelect-select") ||
          document.activeElement.closest(".MuiSelect-select") ||
          document.activeElement.closest(".MuiInputBase-root")
        ) {
          document.activeElement.blur();
        }
      }
    };

    const handleScroll = (event: Event) => {
      const openMenus = document.querySelectorAll(".MuiMenu-root");
      if (!openMenus.length) return;

      // Don't close if the user is scrolling inside the dropdown menu list itself
      for (let i = 0; i < openMenus.length; i++) {
        const menuPaper = openMenus[i].querySelector(".MuiPaper-root");
        if (
          menuPaper &&
          (menuPaper === event.target ||
            menuPaper.contains(event.target as Node))
        ) {
          return;
        }
      }

      openMenus.forEach((menu) => {
        const backdrop = menu.querySelector<HTMLElement>(".MuiBackdrop-root");
        if (backdrop) {
          backdrop.click();
        }
      });

      document.dispatchEvent(
        new KeyboardEvent("keydown", {
          key: "Escape",
          code: "Escape",
          bubbles: true,
          cancelable: true,
        })
      );

      blurActiveSelect();
      setTimeout(blurActiveSelect, 50);
    };

    // When clicking outside/backdrop to dismiss dropdown, blur the select so placeholder returns
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && target.classList.contains("MuiBackdrop-root")) {
        setTimeout(blurActiveSelect, 50);
      }
    };

    window.addEventListener("scroll", handleScroll, true);
    document.addEventListener("pointerdown", handlePointerDown, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, []);

  return (
    <ErrorBoundary>
      <Provider store={store}>
        <GlobalSettingsProvider>
          {/* <ThemeProviderWrapper> */}
          <AppRoutes />
          <Toast />
          {/* </ThemeProviderWrapper> */}
        </GlobalSettingsProvider>
      </Provider>
    </ErrorBoundary>
  );
};

export default App;
